import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { benachrichtigeRechteAenderung } from '../../../hooks/useRechte';
import { gruppiereSchluessel, matrixZeilen } from '../../../lib/rechteMatrix';
import { Benutzer } from '../../../types';
import {
  AKTIONEN, API_URL, STIL, fehlerText, schluesselLabel, werteFuer, wertLabel,
} from './rechteUtil';

type Rollen = Record<string, Record<string, unknown>>;

interface BenutzerRechte {
  rollen: string[];
  overrides: Record<string, string>;
  effektiv: Record<string, string>;
}

const gleicheMenge = (a: string[], b: string[]) =>
  a.length === b.length && a.every(x => b.includes(x));

/** Tab "Benutzer": Rollen eines Benutzers und individuelle Abweichungen (Overrides). */
const RechteBenutzer: React.FC = () => {
  const [benutzer, setBenutzer] = useState<Benutzer[]>([]);
  const [alleRollen, setAlleRollen] = useState<string[]>([]);
  const [alleSchluessel, setAlleSchluessel] = useState<string[]>([]);
  const [benutzerId, setBenutzerId] = useState<string>('');
  const [daten, setDaten] = useState<BenutzerRechte | null>(null);
  const [rollenEntwurf, setRollenEntwurf] = useState<string[]>([]);
  const [overrideEntwurf, setOverrideEntwurf] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [erfolg, setErfolg] = useState<string | null>(null);

  // Stammdaten: Benutzer, Rollen und Rechteschlüssel
  useEffect(() => {
    Promise.all([
      axios.get<Benutzer[]>(`${API_URL}/api/benutzer`),
      axios.get<Rollen>(`${API_URL}/api/rechte/rollen`),
    ])
      .then(([b, r]) => {
        setBenutzer(b.data);
        const namen = Object.keys(r.data);
        setAlleRollen(namen.includes('Admin') ? namen : [...namen, 'Admin']);
        const erste = namen.find(n => n !== 'Admin') ?? namen[0];
        setAlleSchluessel(erste ? Object.keys(r.data[erste]) : []);
      })
      .catch(err => setFehler(fehlerText(err, 'Daten konnten nicht geladen werden.')))
      .finally(() => setLoading(false));
  }, []);

  // Aktuell gewählter Benutzer: Antworten für einen früher gewählten werden verworfen
  const aktuelleAuswahl = useRef('');

  const ladeBenutzer = useCallback((id: string) => {
    if (!id) return Promise.resolve();
    return axios.get<BenutzerRechte>(`${API_URL}/api/rechte/benutzer/${id}`)
      .then(r => {
        if (aktuelleAuswahl.current !== id) return;
        setDaten(r.data);
        setRollenEntwurf(r.data.rollen);
        setOverrideEntwurf(r.data.overrides);
      })
      .catch(err => {
        if (aktuelleAuswahl.current !== id) return;
        setDaten(null);
        setFehler(fehlerText(err, 'Rechte des Benutzers konnten nicht geladen werden.'));
      });
  }, []);

  const waehleBenutzer = (id: string) => {
    aktuelleAuswahl.current = id;
    setBenutzerId(id);
    // Daten und Entwürfe des vorherigen Benutzers verwerfen, bis die neuen geladen sind
    setDaten(null);
    setRollenEntwurf([]);
    setOverrideEntwurf({});
    setFehler(null);
    setErfolg(null);
    ladeBenutzer(id);
  };

  // Alle Schlüssel in lesbarer Reihenfolge: Module (Modul x Aktion), Fähigkeiten, Agenten
  const schluesselListe = useMemo(() => {
    const g = gruppiereSchluessel(alleSchluessel);
    const vorhanden = new Set(alleSchluessel);
    const modulKeys = matrixZeilen(g.module, AKTIONEN).map(z => z.schluessel).filter(k => vorhanden.has(k));
    return [...modulKeys, ...g.faehigkeiten, ...g.agenten];
  }, [alleSchluessel]);

  const rollenGeaendert = !!daten && !gleicheMenge(daten.rollen, rollenEntwurf);

  // Nur geänderte Overrides; leerer Entwurf = Override löschen (null)
  const overrideAenderungen = useMemo(() => {
    const diff: Record<string, string | null> = {};
    if (!daten) return diff;
    for (const k of schluesselListe) {
      const alt = daten.overrides[k];
      const neu = overrideEntwurf[k];
      if (alt === neu) continue;
      diff[k] = neu === undefined ? null : neu;
    }
    return diff;
  }, [daten, overrideEntwurf, schluesselListe]);
  const anzahlAenderungen = Object.keys(overrideAenderungen).length + (rollenGeaendert ? 1 : 0);

  const toggleRolle = (rolle: string) => {
    setRollenEntwurf(r => (r.includes(rolle) ? r.filter(x => x !== rolle) : [...r, rolle]));
  };

  const setOverride = (schluessel: string, wert: string) => {
    setOverrideEntwurf(o => {
      const n = { ...o };
      if (wert === '') delete n[schluessel];
      else n[schluessel] = wert;
      return n;
    });
  };

  const speichern = async () => {
    if (!daten) return;
    if (rollenEntwurf.length === 0) {
      setFehler('Mindestens eine Rolle ist erforderlich.');
      return;
    }
    setSaving(true);
    setFehler(null);
    setErfolg(null);
    try {
      const body: { rollen?: string[]; overrides?: Record<string, string | null> } = {};
      if (rollenGeaendert) body.rollen = rollenEntwurf;
      if (Object.keys(overrideAenderungen).length > 0) body.overrides = overrideAenderungen;
      await axios.put(`${API_URL}/api/rechte/benutzer/${benutzerId}`, body);
      benachrichtigeRechteAenderung();
      await ladeBenutzer(benutzerId);
      setErfolg('Gespeichert.');
    } catch (err) {
      setFehler(fehlerText(err, 'Speichern fehlgeschlagen.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div>Lade Benutzer…</div>;

  return (
    <div>
      {fehler && <div style={STIL.fehler}>{fehler}</div>}
      {erfolg && <div style={{ ...STIL.hinweis, color: 'var(--green-500)' }}>{erfolg}</div>}

      <label style={{ ...STIL.label, maxWidth: 360, marginBottom: 16 }}>
        Benutzer
        <select value={benutzerId} onChange={e => waehleBenutzer(e.target.value)} style={STIL.select}>
          <option value="">— Benutzer wählen —</option>
          {benutzer.map(b => (
            <option key={b.benutzer_id} value={b.benutzer_id}>{b.name} ({b.email})</option>
          ))}
        </select>
      </label>

      {daten && (
        <>
          <div style={STIL.untertitel}>Rollen</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px' }}>
            {alleRollen.map(r => (
              <label key={r} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={rollenEntwurf.includes(r)}
                  disabled={saving}
                  onChange={() => toggleRolle(r)}
                />
                {r}
              </label>
            ))}
          </div>
          {rollenGeaendert && (
            <div style={{ ...STIL.hinweis, marginTop: 8 }}>
              Die effektiven Rechte unten werden nach dem Speichern neu berechnet.
            </div>
          )}

          <div style={STIL.untertitel}>Abweichungen von den Rollenrechten</div>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <th style={STIL.th}>Recht</th>
                <th style={STIL.th}>Effektiv</th>
                <th style={STIL.th}>Abweichung</th>
              </tr>
            </thead>
            <tbody>
              {schluesselListe.map(k => {
                const override = overrideEntwurf[k] ?? '';
                return (
                  <tr key={k}>
                    <td style={STIL.td}>{schluesselLabel(k)}</td>
                    <td style={STIL.td}>{wertLabel(daten.effektiv[k] ?? '—')}</td>
                    <td style={STIL.td}>
                      <select
                        value={override}
                        disabled={saving}
                        onChange={e => setOverride(k, e.target.value)}
                        style={STIL.select}
                        aria-label={`Abweichung ${schluesselLabel(k)}`}
                      >
                        <option value="">— wie Rolle —</option>
                        {werteFuer(k).map(w => <option key={w} value={w}>{wertLabel(w)}</option>)}
                      </select>
                      {override !== '' && <span style={STIL.badge}>weicht von Rolle ab</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div style={{ marginTop: 20 }}>
            <button
              type="button"
              className="k-btn k-btn--primary"
              disabled={saving || !daten || anzahlAenderungen === 0}
              onClick={speichern}
            >
              {saving ? 'Speichert…' : 'Speichern'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default RechteBenutzer;
